import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment-timezone'
import { withTranslation } from 'react-i18next'

import { RadioButton } from '../../../../../component-library/components/radio-button/radio-button.component'
import { getMomentTimeZone } from '../../../../../service/locale/timezone'
import { DELIVERY_QUOTATION_TYPES } from './dropoff-time.constants'
import {
  StyledWrapper,
  StyledSubheading,
  StyledRadioGroup,
} from '../../order-form.component.styles'
import {
  StyledExternalLinkIcon,
  StyledTextLink,
  FlexContainer,
  ErrorText,
} from './dropoff-time.component.styles'
import { orderTrackingService } from '../../../tracking.service'

const currentTimeZone = getMomentTimeZone()

const quotationTypeShape = PropTypes.shape({
  shipperRegionSupported: PropTypes.bool,
  shipperRegion: PropTypes.string,
  recipientRegionSupported: PropTypes.bool,
  recipientRegion: PropTypes.string,
  pickupTimeValid: PropTypes.bool,
  dropoffTime: PropTypes.string,
})

class DropoffTime extends React.Component {
  static titleId = 'delivery-dropoff-time-heading'

  static propTypes = {
    input: PropTypes.shape({
      value: PropTypes.string,
      onChange: PropTypes.func,
    }),
    t: PropTypes.func,
    meta: PropTypes.shape({
      touched: PropTypes.bool.isRequired,
      error: PropTypes.string.isRequired,
    }),
    instant: quotationTypeShape,
    speed: quotationTypeShape,
    sameDayDay: quotationTypeShape,
    sameDayNight: quotationTypeShape,
  }

  state = {
    selectedOption: null,
    invalidSelectionError: false,
  }

  getTranslations() {
    const { t } = this.props

    return {
      dropoffTimeTitle: t('orders.new.basicInfo.subtitle.dropoffTime'),
      fastestLabel: t('orders.new.basicInfo.subtitle.fastestDropoffOption'),
      fourHourLabel: t('orders.new.basicInfo.selection.4hour'),
      option6pmLabel: t('orders.new.basicInfo.selection.dropoffBy', {
        time: '18:00',
      }),
      disabled6pmLabel: t(
        'orders.new.basicInfo.subtitle.dropoffOptionAvailabilityCutoff',
        {
          time: '11:00',
        }
      ),
      optionNightLabel: '18:00–22:00',
      disabledNightLabel: t(
        'orders.new.basicInfo.subtitle.dropoffOptionAvailabilityCutoff',
        {
          time: '16:00',
        }
      ),
      optionInstantLabel: t('orders.new.basicInfo.selection.instant'),
      selectedOptionUnavailable: t(
        'orders.new.basicInfo.error.selectedDropoffOptionUnavailable'
      ),
      outsideServiceArea: region =>
        t('helper.ggd.dropOffTime.OutsideServiceArea.singular', { region }),
      outsideServiceAreaPlural: ({ region1, region2 }) =>
        t('helper.ggd.dropOffTime.OutsideServiceArea.plural', {
          region1,
          region2,
        }),
      fourHourHelperText: time =>
        t('orders.new.basicInfo.selection.dropoffBy', { time }),
    }
  }

  /**
   * @param {string} option
   */
  handleDropOffTimeSelect = option => {
    const { input } = this.props

    this.setState({ selectedOption: option, invalidSelectionError: false })
    switch (option) {
      case DELIVERY_QUOTATION_TYPES.INSTANT:
        return input.onChange(DELIVERY_QUOTATION_TYPES.INSTANT)
      case DELIVERY_QUOTATION_TYPES.SPEED:
        return input.onChange(DELIVERY_QUOTATION_TYPES.SPEED)
      case DELIVERY_QUOTATION_TYPES.SAMEDAY_DAY:
        return input.onChange(DELIVERY_QUOTATION_TYPES.SAMEDAY_DAY)
      case DELIVERY_QUOTATION_TYPES.SAMEDAY_NIGHT:
        return input.onChange(DELIVERY_QUOTATION_TYPES.SAMEDAY_NIGHT)
      default:
        return input.onChange(null)
    }
  }

  _getQuotationTypesSupported() {
    const { instant, speed, sameDayDay, sameDayNight } = this.props
    const supportInstant =
      instant.shipperRegionSupported && instant.recipientRegionSupported
    const supportSpeed =
      speed.shipperRegionSupported &&
      speed.recipientRegionSupported &&
      speed.pickupTimeValid
    const supportSameDayDay =
      sameDayDay.shipperRegionSupported &&
      sameDayDay.recipientRegionSupported &&
      sameDayDay.pickupTimeValid
    const supportSameDayNight =
      sameDayNight.shipperRegionSupported &&
      sameDayNight.recipientRegionSupported &&
      sameDayNight.pickupTimeValid

    return {
      supportInstant:
        typeof supportInstant === 'boolean' ? supportInstant : true,
      supportSpeed: typeof supportSpeed === 'boolean' ? supportSpeed : true,
      supportSameDayDay:
        typeof supportSameDayDay === 'boolean' ? supportSameDayDay : true,
      supportSameDayNight:
        typeof supportSameDayNight === 'boolean' ? supportSameDayNight : true,
    }
  }

  _getHelperText(quotationType) {
    const translations = this.getTranslations()
    const deliveryType = this.props[quotationType]
    const { dropoffTime } = deliveryType

    const getOutsideServiceAreaText = () => {
      if (
        deliveryType.shipperRegionSupported === false &&
        deliveryType.recipientRegionSupported === false
      ) {
        return translations.outsideServiceAreaPlural({
          region1: deliveryType.shipperRegion,
          region2: deliveryType.recipientRegion,
        })
      }
      if (deliveryType.shipperRegionSupported === false) {
        return translations.outsideServiceArea(deliveryType.shipperRegion)
      }
      if (deliveryType.recipientRegionSupported === false) {
        return translations.outsideServiceArea(deliveryType.recipientRegion)
      }
      return ''
    }

    const getPickupTimeMustBeBeforeText = () => {
      if (
        deliveryType.pickupTimeValid === false &&
        quotationType === 'sameDayDay'
      ) {
        return translations.disabled6pmLabel
      }
      if (
        deliveryType.pickupTimeValid === false &&
        quotationType === 'sameDayNight'
      ) {
        return translations.disabledNightLabel
      }
      return ''
    }

    const get4HourPickupByText = () => {
      if (deliveryType.pickupTimeValid && quotationType === 'speed') {
        const formattedDropoffTime = moment
          .tz(dropoffTime, moment.ISO_8601, currentTimeZone)
          .format('HH:mm')

        const ROUND_UP_TO_TEN = 10 * 60 * 1000
        const epoch = dropoffTime && moment(dropoffTime).valueOf()
        let roundedTime = moment(
          Math.ceil(epoch / ROUND_UP_TO_TEN) * ROUND_UP_TO_TEN
        )
        roundedTime = moment
          .tz(roundedTime, moment.ISO_8601, currentTimeZone)
          .format('HH:mm')

        return formattedDropoffTime.endsWith('0')
          ? translations.fourHourHelperText(formattedDropoffTime)
          : translations.fourHourHelperText(roundedTime)
      }
      return ''
    }
    return (
      getOutsideServiceAreaText() ||
      getPickupTimeMustBeBeforeText() ||
      get4HourPickupByText()
    )
  }

  _handleInvalidSelection() {
    setTimeout(() => {
      this.handleDropOffTimeSelect(null)
      this.setState({ invalidSelectionError: true })
    })
  }

  _renderInstantDeliveryInformation() {
    const { t } = this.props
    const instantDeliveryURL = t('delivery__gogox_rebranding_url_instant')

    return (
      <StyledTextLink
        href={instantDeliveryURL}
        target="_blank"
        onClick={() =>
          orderTrackingService.trackTextLinkClicked({
            url: instantDeliveryURL,
            urlName: 'Instant Delivery Info',
          })
        }
      >
        <FlexContainer>
          {t('orders.new.basicInfo.moreInfo.whatIsInstantDelivery')}
          <StyledExternalLinkIcon />
        </FlexContainer>
      </StyledTextLink>
    )
  }

  render() {
    const { meta = {} } = this.props
    const { selectedOption, invalidSelectionError } = this.state
    const translations = this.getTranslations()
    const {
      supportInstant,
      supportSpeed,
      supportSameDayDay,
      supportSameDayNight,
    } = this._getQuotationTypesSupported()
    const invalidSelection =
      (selectedOption === DELIVERY_QUOTATION_TYPES.INSTANT &&
        !supportInstant) ||
      (selectedOption === DELIVERY_QUOTATION_TYPES.SPEED && !supportSpeed) ||
      (selectedOption === DELIVERY_QUOTATION_TYPES.SAMEDAY_DAY &&
        !supportSameDayDay) ||
      (selectedOption === DELIVERY_QUOTATION_TYPES.SAMEDAY_NIGHT &&
        !supportSameDayNight)

    if (invalidSelection) {
      this._handleInvalidSelection()
    }

    return (
      <StyledWrapper>
        <StyledSubheading id={DropoffTime.titleId}>
          {translations.dropoffTimeTitle}
        </StyledSubheading>
        <StyledSubheading>
          {this._renderInstantDeliveryInformation()}
        </StyledSubheading>
        <StyledRadioGroup
          across={4}
          onSelectionChange={this.handleDropOffTimeSelect}
          titleId={DropoffTime.titleId}
          value={selectedOption}
          meta={meta}
        >
          <RadioButton
            value={DELIVERY_QUOTATION_TYPES.INSTANT}
            disabled={!supportInstant}
            helperText={this._getHelperText('instant')}
          >
            {translations.optionInstantLabel}
          </RadioButton>
          <RadioButton
            value={DELIVERY_QUOTATION_TYPES.SPEED}
            helperText={this._getHelperText('speed')}
            disabled={!supportSpeed}
          >
            {translations.fourHourLabel}
          </RadioButton>
          <RadioButton
            value={DELIVERY_QUOTATION_TYPES.SAMEDAY_DAY}
            disabled={!supportSameDayDay}
            helperText={this._getHelperText('sameDayDay')}
          >
            {translations.option6pmLabel}
          </RadioButton>
          <RadioButton
            value={DELIVERY_QUOTATION_TYPES.SAMEDAY_NIGHT}
            disabled={!supportSameDayNight}
            helperText={this._getHelperText('sameDayNight')}
          >
            {translations.optionNightLabel}
          </RadioButton>
        </StyledRadioGroup>
        {invalidSelectionError && (
          <ErrorText>{translations.selectedOptionUnavailable}</ErrorText>
        )}
      </StyledWrapper>
    )
  }
}

const DropoffTimeWithTranslation = withTranslation()(DropoffTime)

export { DropoffTimeWithTranslation }
